/**
 * Keyboard shortcut resolver — PURE. Maps a normalized key event to an
 * editor action id, following the Figma / Illustrator / Sketch standard
 * bindings (no invented chords). Platform normalization happens at the
 * event boundary: callers fold macOS ⌘ and Windows/Linux Ctrl into ONE
 * `mod` flag before resolving, so the table below is platform-agnostic.
 *
 * The resolver is data → data (no DOM, no React) so the full matrix is
 * unit-testable in node.
 *
 * @module floorplan/keyboard/shortcuts
 * @category FloorPlan
 */

export type NormalizedKeyEvent = {
  /** `event.key`, lowercased for letters (e.g. 'z', 'arrowleft', 'escape'). */
  key: string;
  /** Ctrl (Win/Linux) OR Cmd (macOS) — the cross-platform "mod". */
  mod: boolean;
  shift: boolean;
  alt: boolean;
};

export type ShortcutAction =
  | 'undo'
  | 'redo'
  | 'copy'
  | 'cut'
  | 'paste'
  | 'duplicate'
  | 'delete'
  | 'group'
  | 'ungroup'
  | 'bring-forward'
  | 'send-backward'
  | 'bring-front'
  | 'send-back'
  | 'select-all'
  | 'deselect-all'
  | 'escape'
  | 'nudge-left'
  | 'nudge-right'
  | 'nudge-up'
  | 'nudge-down'
  | 'nudge-left-big'
  | 'nudge-right-big'
  | 'nudge-up-big'
  | 'nudge-down-big'
  | 'zoom-in'
  | 'zoom-out'
  | 'zoom-fit'
  | 'zoom-100'
  | 'tool-move'
  | 'tool-hand'
  | 'tool-marquee'
  | 'tool-rect'
  | 'tool-ellipse'
  | 'tool-line'
  | 'tool-text';

const ARROW_ACTIONS: Record<string, [ShortcutAction, ShortcutAction]> = {
  arrowleft: ['nudge-left', 'nudge-left-big'],
  arrowright: ['nudge-right', 'nudge-right-big'],
  arrowup: ['nudge-up', 'nudge-up-big'],
  arrowdown: ['nudge-down', 'nudge-down-big'],
};

/** Bare-key tool hotkeys (Figma/Illustrator letters). */
const TOOL_KEYS: Record<string, ShortcutAction> = {
  v: 'tool-move',
  h: 'tool-hand',
  m: 'tool-marquee',
  r: 'tool-rect',
  o: 'tool-ellipse',
  l: 'tool-line',
  t: 'tool-text',
};

/**
 * Resolve a normalized key event to an action, or null when unbound.
 * Precedence: mod-chords → arrows → bare keys.
 */
export const resolveShortcut = (
  event: NormalizedKeyEvent,
): ShortcutAction | null => {
  const key = event.key;

  if (event.mod) {
    if (key === 'z') return event.shift ? 'redo' : 'undo';
    if (key === 'y') return 'redo';
    if (key === 'c') return 'copy';
    if (key === 'x') return 'cut';
    if (key === 'v') return 'paste';
    if (key === 'd') return 'duplicate';
    if (key === 'g') return event.shift ? 'ungroup' : 'group';
    if (key === ']') return event.shift ? 'bring-front' : 'bring-forward';
    if (key === '[') return event.shift ? 'send-back' : 'send-backward';
    if (key === 'a') return event.shift ? 'deselect-all' : 'select-all';
    if (key === '=' || key === '+') return 'zoom-in';
    if (key === '-') return 'zoom-out';
    if (key === '0') return 'zoom-fit';
    if (key === '1') return 'zoom-100';
    return null;
  }

  if (key === 'escape') return 'escape';
  if (key === 'delete' || key === 'backspace') return 'delete';

  const arrow = ARROW_ACTIONS[key];
  if (arrow !== undefined) {
    if (event.shift) return arrow[1];
    return arrow[0];
  }

  if (!event.shift && !event.alt) {
    const tool = TOOL_KEYS[key];
    if (tool !== undefined) return tool;
  }

  return null;
};

/**
 * Fold a raw DOM KeyboardEvent-shaped object into the normalized form.
 * `isMac` decides which of metaKey/ctrlKey is the cross-platform mod —
 * on macOS Ctrl-chords are left to the browser (⌃ is not a Figma mod),
 * and on Windows/Linux the Meta (Win) key is ignored.
 */
export const normalizeKeyEvent = (
  raw: {
    key: string;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
  },
  isMac: boolean,
): NormalizedKeyEvent => ({
  key: raw.key.toLowerCase(),
  mod: isMac ? raw.metaKey : raw.ctrlKey,
  shift: raw.shiftKey,
  alt: raw.altKey,
});

/**
 * Display label for the cross-platform mod key — for tooltip hints
 * ("⌘D" on macOS, "Ctrl+D" elsewhere). Safe anywhere (guards navigator).
 */
export const modKeyLabel = (): string => {
  if (typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)) {
    return '⌘';
  }
  return 'Ctrl+';
};
